import { useAuthStore } from '@/state/store/auth';
import { getRefreshToken } from '@/modules/auth/services/auth.service';
import { isLocalhost } from './utils/localhost-checker/locahost-checker';

interface Https {
  get<T>(url: string, headers?: HeadersInit): Promise<T>;
  post<T>(url: string, body: BodyInit, headers?: HeadersInit): Promise<T>;
  put<T>(url: string, body: BodyInit, headers?: HeadersInit): Promise<T>;
  delete<T>(url: string, headers?: HeadersInit): Promise<T>;
  stream(
    url: string,
    body: BodyInit,
    headers?: HeadersInit
  ): Promise<ReadableStream<Uint8Array>>;
  request<T>(url: string, options: RequestOptions): Promise<T>;
  createHeaders(headers: any): Headers;
  refreshAccessToken(): Promise<void>;
}

interface RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: HeadersInit;
  body?: BodyInit;
}

export class HttpError extends Error {
  status: number;
  error: Record<string, unknown>;

  constructor(status: number, error: Record<string, unknown>) {
    const errorMessage = typeof error.message === 'string' ? error.message : JSON.stringify(error);

    super(errorMessage);
    this.status = status;
    this.error = error;
  }
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '');
const projectKey = import.meta.env.VITE_X_BLOCKS_KEY ?? '';
const localHostChecker = isLocalhost();

export const clients: Https = {
  async get<T>(url: string, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'GET', headers });
  },

  async post<T>(url: string, body: BodyInit, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'POST', headers, body });
  },

  async put<T>(url: string, body: BodyInit, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'PUT', headers, body });
  },

  async delete<T>(url: string, headers: HeadersInit = {}): Promise<T> {
    return this.request<T>(url, { method: 'DELETE', headers });
  },

  async stream(
    url: string,
    body: BodyInit,
    headers: HeadersInit = {}
  ): Promise<ReadableStream<Uint8Array>> {
    const fullUrl = url.startsWith('http') ? url : `${BASE_URL}/${url.replace(/^\//, '')}`;
    const requestHeaders = this.createHeaders(headers);

    const config: RequestInit = {
      method: 'POST',
      headers: requestHeaders,
      body,
      referrerPolicy: 'no-referrer',
      credentials: !localHostChecker ? 'include' : undefined,
    };

    let response: Response;
    try {
      response = await fetch(fullUrl, config);

      if (response.status === 401) {
        await this.refreshAccessToken();
        return this.stream(url, body, headers);
      }

      if (!response.ok || !response.body) {
        throw new HttpError(response.status, { error: 'Streaming failed' });
      }
    } catch (err) {
      if (err instanceof HttpError) throw err;
      throw new HttpError(500, { error: 'Network error' });
    }

    return response.body;
  },

  async request<T>(url: string, { method, headers = {}, body }: RequestOptions): Promise<T> {
    const fullUrl = url.startsWith('http') ? url : `${BASE_URL}/${url.replace(/^\//, '')}`;

    const requestHeaders = this.createHeaders(headers);

    const config: RequestInit = {
      method,
      headers: requestHeaders,
      referrerPolicy: 'no-referrer',
    };

    if (!localHostChecker) {
      config.credentials = 'include';
    }

    if (body) {
      config.body = body;
    }

    try {
      const response = await fetch(fullUrl, config);

      if (response.ok) {
        return response.json() as Promise<T>;
      }

      if (response.status === 401) {
        await this.refreshAccessToken();
        return this.request<T>(url, { method, headers, body });
      }

      const err = await response.json();
      throw new HttpError(response.status, err);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new HttpError(500, { error: 'Network error' });
    }
  },

  createHeaders(headers: any): Headers {
    const authToken = localHostChecker ? useAuthStore.getState().accessToken : null;

    const baseHeaders = {
      'Content-Type': 'application/json',
      'x-blocks-key': projectKey,
      ...(authToken && { Authorization: `bearer ${authToken}` }),
    };

    const headerEntries =
      headers instanceof Headers ? Object.fromEntries(headers.entries()) : headers;

    const newHeader = new Headers({
      ...baseHeaders,
      ...headerEntries,
    });
    return newHeader;
  },

  async refreshAccessToken(): Promise<void> {
    const authStore = useAuthStore.getState();
    try {
      if (!authStore.refreshToken) throw new HttpError(401, { error: 'invalid_request' });
      const refreshTokenRes = await getRefreshToken();
      if (refreshTokenRes.error === 'invalid_refresh_token') {
        authStore.logout();
        throw new HttpError(401, refreshTokenRes);
      }
      authStore.setAccessToken(refreshTokenRes.access_token);
    } catch (error) {
      authStore.logout();
      throw error;
    }
  },
};
